import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getApprovedBlogPosts, searchBlogPosts, getBlogPostsByCategory } from '../services/firebase/blogService';
import './BlogListingPage.css';

const BlogListingPage = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  const categories = [
    { id: 'all', label: 'All Articles' },
    { id: 'art', label: '🎨 Art & Design' },
    { id: 'tutorial', label: '📚 Tutorials' },
    { id: 'trends', label: '📈 Trends' },
    { id: 'community', label: '👥 Community' },
    { id: 'news', label: '📰 News' },
    { id: 'tips', label: '💡 Tips & Tricks' },
  ];

  useEffect(() => {
    loadPosts();
  }, []);

  useEffect(() => {
    filterAndSortPosts();
  }, [posts, searchTerm, selectedCategory, sortBy]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const result = await getApprovedBlogPosts(100);
      
      if (result.success) {
        setPosts(result.data);
      } else {
        setError('Failed to load blog posts');
      }
    } catch (err) {
      setError('Error loading posts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortPosts = () => {
    let filtered = posts;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    // Sort
    if (sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.createdAt?.toDate?.() || b.createdAt) - new Date(a.createdAt?.toDate?.() || a.createdAt));
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => new Date(a.createdAt?.toDate?.() || a.createdAt) - new Date(b.createdAt?.toDate?.() || b.createdAt));
    } else if (sortBy === 'popular') {
      filtered.sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0));
    }

    setFilteredPosts(filtered);
  };

  return (
    <div className="blog-listing-page">
      {/* Header */}
      <div className="blog-header">
        <div className="blog-header-content">
          <h1>📚 Blog & Community</h1>
          <p>Discover insights, tutorials, and stories from our thriving art community</p>
        </div>
      </div>

      <div className="blog-container">
        {/* Sidebar */}
        <aside className="blog-sidebar">
          {/* Search */}
          <div className="sidebar-section">
            <h3>Search</h3>
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Categories */}
          <div className="sidebar-section">
            <h3>Categories</h3>
            <div className="categories-list">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  className={`category-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  {cat.label}
                  <span className="count">
                    ({posts.filter(p => cat.id === 'all' || p.category === cat.id).length})
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Sort */}
          <div className="sidebar-section">
            <h3>Sort By</h3>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="popular">Most Popular</option>
            </select>
          </div>
        </aside>

        {/* Main Content */}
        <main className="blog-main">
          {error && (
            <div className="alert alert-error">
              ❌ {error}
            </div>
          )}

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading articles...</p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="empty-state">
              <p className="empty-icon">📭</p>
              <h3>No articles found</h3>
              <p>
                {searchTerm
                  ? `No articles match "${searchTerm}"`
                  : 'No articles in this category yet'}
              </p>
              {searchTerm && (
                <button
                  className="btn-clear-search"
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="results-header">
                <p>{filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''} found</p>
              </div>

              <div className="posts-list">
                {filteredPosts.map(post => (
                  <article key={post.id} className="post-list-item">
                    {post.featuredImage && (
                      <Link to={`/blog/${post.id}`} className="post-list-image">
                        <img src={post.featuredImage} alt={post.title} />
                      </Link>
                    )}

                    <div className="post-list-content">
                      <div className="post-list-meta">
                        <span className="category-badge">
                          {categories.find(c => c.id === post.category)?.label}
                        </span>
                        <span className="post-date">
                          {new Date(post.createdAt?.toDate?.() || post.createdAt).toLocaleDateString()}
                        </span>
                      </div>

                      <Link to={`/blog/${post.id}`} className="post-list-title-link">
                        <h2 className="post-list-title">{post.title}</h2>
                      </Link>

                      <p className="post-list-excerpt">{post.excerpt}</p>

                      <div className="post-list-footer">
                        <div className="post-author-info">
                          {post.vendorAvatar && (
                            <img
                              src={post.vendorAvatar}
                              alt={post.vendorName}
                              className="author-avatar-small"
                            />
                          )}
                          <span className="author-name">{post.vendorName}</span>
                        </div>

                        <div className="post-stats">
                          <span>👁️ {post.viewCount || 0}</span>
                          <span>❤️ {post.likes || 0}</span>
                          <span>💬 {post.comments || 0}</span>
                        </div>

                        <Link to={`/blog/${post.id}`} className="continue-reading">
                          Continue Reading →
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default BlogListingPage;
